/*
This software is Copyright ©️ 2020 The University of Southern California. All Rights Reserved. 
Permission to use, copy, modify, and distribute this software and its documentation for educational, research and non-profit purposes, without fee, and without a written agreement is hereby granted, provided that the above copyright notice and subject to the full license file found in the root of this software deliverable. Permission to make commercial use of this software may be obtained by contacting:  USC Stevens Center for Innovation University of Southern California 1150 S. Olive Street, Suite 2300, Los Angeles, CA 90115, USA Email: accounting@stevens.usc.edu

The full terms of this copyright and license should always be found in the root directory of this software deliverable as "license.txt" and if these terms are not found with this software, please contact the USC Stevens Center for the full license.
*/

import * as actions from ".";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { Edits, KnowledgeComponent, LoadStatus } from "../../../types";

export interface UseWithKnowledgeComponents {
  knowledgeComponents: Record<string, KnowledgeComponent>;
  knowledgeComponentEdits: Record<string, Edits<KnowledgeComponent>>;
  knowledgeComponentLoadStatus: LoadStatus;
  loadAllKnowledgeComponents: () => Promise<KnowledgeComponent[]>;
  createKnowledgeComponent: (
    newData: Partial<KnowledgeComponent>
  ) => Promise<KnowledgeComponent>;
  deleteKnowledgeComponent: (
    knowledgeComponent: KnowledgeComponent
  ) => Promise<void>;
}

export default function useWithKnowledgeComponents(): UseWithKnowledgeComponents {
  const {
    knowledgeComponents,
    knowledgeComponentLoadStatus,
    knowledgeComponentEdits,
  } = useAppSelector((state) => state.knowledgeComponents);
  const dispatch = useAppDispatch();

  async function loadAllKnowledgeComponents(): Promise<KnowledgeComponent[]> {
    if (knowledgeComponentLoadStatus === LoadStatus.IN_PROGRESS) return [];
    return await dispatch(actions.loadKnowledgeComponents()).unwrap();
  }

  async function createKnowledgeComponent(
    data: Partial<KnowledgeComponent>
  ): Promise<KnowledgeComponent> {
    if (!data.name) throw new Error("name required");
    return await dispatch(
      actions.addOrUpdateKnowledgeComponent({
        id: data.id || "",
        name: data.name || "",
        desc: data.desc || "",
        deleted: Boolean(data.deleted),
      })
    ).unwrap();
  }

  async function deleteKnowledgeComponent(
    knowledgeComponent: KnowledgeComponent
  ): Promise<void> {
    await dispatch(actions.deleteKnowledgeComponent(knowledgeComponent.id));
  }

  return {
    knowledgeComponents,
    knowledgeComponentEdits,
    knowledgeComponentLoadStatus,
    loadAllKnowledgeComponents,
    createKnowledgeComponent,
    deleteKnowledgeComponent,
  };
}
